import { ChannelType } from '@aaa-ncnu-ie/mypolicy-api-registrations';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useOrganicRegistrationForm } from '..';
import { useSendCodeMutation } from '../../../../../api/registration/otvc/sendCode';
import { useValidateCodeMutation } from '../../../../../api/registration/otvc/verifyCode';
import Verification from '../../../../../assets/icons/Verification';
import ControlledTextField from '../../../../../components/TextField/ControlledTextField';
import Button from '../../../../../components/styledComponents/Button';
import { LinkWithNoRouter } from '../../../../../components/styledComponents/Link';
import colors from '../../../../../constants/colors';
import { useDirectAccessAnalytics } from '../../../../../hooks/useAnalytics';
import { useSnackbarsContext } from '../../../../../store/SnackbarProvider';
import PhoneNumberMask from '../../../Components/PhoneNumberMask';
import SplitView from '../../../SplitView';
import {
  OneTimeVerificationSms,
  oneTimeVerificationSmsSchema,
} from '../../schemas/oneTimeVerificationCode';

const obfuscateUserPhoneNumber = (phoneNumberLast2?: string) => {
  if (!phoneNumberLast2) {
    return '';
  }

  return `${'*'.repeat(8)}${phoneNumberLast2}`;
};

const SmsVerification = () => {
  const navigate = useNavigate();
  const { formValues, otvcValues, setOtvcValues } =
    useOrganicRegistrationForm();
  const sendCodeMutation = useSendCodeMutation();
  const validateCodeMutation = useValidateCodeMutation();
  const { closeSnackbar, enqueueSnackbar } = useSnackbarsContext();
  const obfuscatedPhoneNumber = obfuscateUserPhoneNumber(
    otvcValues.phoneNumber
  );
  const { track } = useDirectAccessAnalytics();
  const [submitted, setSubmitted] = useState(false);
  const {
    control,
    formState: { errors, isValid },
    getValues,
    handleSubmit,
    setValue,
    resetField,
  } = useForm<OneTimeVerificationSms>({
    resolver: yupResolver(oneTimeVerificationSmsSchema(submitted)), // placeholder phone number that will come from API
    mode: 'onBlur',
  });

  const showResentCodeBanner = () => {
    track('User Messaged', {
      event_type: 'Messaged',
      location: 'Feature Card',
      message_type: 'Info',
      contents: 'A new code has been sent to your phone.',
    });
    enqueueSnackbar({
      autoHideDuration: 10000,
      content: 'A new code has been sent to your phone.',
      key: 'resent-sms-code',
      variant: 'success',
    });
  };

  const showGenericErrorBanner = () => {
    track('User Messaged', {
      event_type: 'Messaged',
      location: 'Feature Card',
      message_type: 'Error',
      contents: 'We are unable to process your request right now.',
    });
    enqueueSnackbar({
      key: 'policy-search-organic-registration-error',
      variant: 'error',
      title: 'We are unable to process your request right now.',
      content: (
        <>
          Please check the details and try again, or contact Service at {}
          <LinkWithNoRouter
            href="tel:+888.980.5650"
            underline="none"
            sx={{
              fontSize: {
                mobile: 16,
                tablet: 18,
              },
              fontWeight: 400,
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaSpectrumBlue50,
            }}
          >
            888.980.5650
          </LinkWithNoRouter>
          .
        </>
      ),
    });
  };

  const requestCode = async (phoneNumber: string) => {
    closeSnackbar();

    const formattedPhoneNumber = phoneNumber.replaceAll(/\(|\)|-/g, '');

    try {
      await sendCodeMutation.mutateAsync({
        channelType: ChannelType.SMS,
        channelValue: formattedPhoneNumber,
        emailId: formValues.email,
        lastName: formValues.lastName,
      });

      if (submitted) {
        showResentCodeBanner();
      } else {
        setSubmitted(true);
      }

      if (!otvcValues?.codeSent) {
        setOtvcValues((current) => ({
          ...current,
          codeSent: true,
          flow: 'SMS',
        }));
      }
    } catch (err) {
      showGenericErrorBanner();
      if (!submitted) {
        resetField('phoneNumber');
      }
    }
  };

  const verifyCode = async (phoneNumber: string, verificationCode: string) => {
    closeSnackbar();
    const formattedPhoneNumber = phoneNumber.replaceAll(/\(|\)|-/g, '');

    try {
      const res = await validateCodeMutation.mutateAsync({
        id: formattedPhoneNumber!,
        otp: verificationCode.toString(),
      });
      if (res.otpValidated) {
        navigate('/register/code/verification', { replace: true });
      } else {
        setValue('verificationCode', '');
        showGenericErrorBanner();
      }
    } catch (err) {
      showGenericErrorBanner();
    }
  };

  const trackValidationErrors = (fieldName?: string) => {
    if (fieldName) {
      if (fieldName in errors) {
        track('Input Validated', {
          event_type: 'Automated System Process',
          location: 'Feature Card',
          registration_type: 'OTVC',
          field_name: fieldName,
          error_message:
            errors[fieldName as keyof OneTimeVerificationSms]!.message,
          status: 'Failed',
        });
      }
    } else {
      for (const errorField in errors) {
        track('Input Validated', {
          event_type: 'Automated System Process',
          location: 'Feature Card',
          registration_type: 'OTVC',
          field_name: errorField,
          error_message:
            errors[errorField as keyof OneTimeVerificationSms]!.message,
          status: 'Failed',
        });
      }
    }
  };

  const trackInputBlurred = (fieldName?: string) => {
    const values = getValues();
    if (fieldName && values[fieldName as keyof OneTimeVerificationSms]) {
      track('Field Information Entered', {
        event_type: 'User Information Entered',
        location: 'Feature Card',
        registration_type: 'OTVC',
        field_name: fieldName,
        input: values[fieldName as keyof OneTimeVerificationSms]!,
      });
    }
  };

  const inputBlurred = (fieldName?: string) => {
    trackValidationErrors(fieldName);
    trackInputBlurred(fieldName);
  };

  const disableContinue =
    !isValid || sendCodeMutation.isPending || validateCodeMutation.isPending;

  useEffect(() => {
    track('Verification Method Selected', {
      event_type: 'Option Selected',
      location: 'Feature Card',
      registration_type: 'OTVC',
      flow: 'SMS',
      email: formValues.email,
      login_method: 'DA',
    });

    if (!otvcValues?.smsEligible || !otvcValues?.phoneNumber) {
      track('Registration Failed', {
        event_type: 'Automated System Process',
        location: 'Feature Card',
        registration_type: 'OTVC',
        flow: 'SMS',
        reason: 'Not eligible',
        login_method: 'DA',
      });
      navigate('/register', { replace: true });
    }
  }, []);

  const onSubmit = async ({
    phoneNumber,
    verificationCode,
  }: OneTimeVerificationSms) => {
    if (submitted) {
      await verifyCode(phoneNumber, verificationCode);
    } else {
      await requestCode(phoneNumber);
    }
  };

  return (
    <SplitView data-testid="organic-registration-1" Icon={<Verification />}>
      <Stack
        direction="column"
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          justifyContent: { mobile: 'flex-start', desktop: 'center' },
          px: { mobile: 2, tablet: 14, desktop: 11 },
          height: '100%'
        }}>
        <Typography
          variant="h1"
          sx={{
            fontWeight: 700,
            fontSize: { mobile: 33, tablet: 44 },
            lineHeight: { mobile: '48px', desktop: '56px' },
          }}
        >
          Verification code
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 500,
            fontSize: { mobile: 23, tablet: 28 },
            lineHeight: { mobile: '32px', desktop: '40px' },
            mt: 1,
            mb: 2,
            color: colors.csaaDark,
          }}
        >
          {!submitted
            ? "We'll send you a one-time code to help you sign up."
            : 'Check your mobile phone for the code. The code is valid for 30 minutes.'}
        </Typography>
        {submitted ? (
          <Stack direction="column" spacing={1} sx={{ mt: 3 }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'Cabin',
                fontWeight: 400,
                fontSize: { mobile: 14, tablet: 16 },
                lineHeight: '24px',
                color: colors.csaaDark,
              }}
            >
              Enter the code we sent to {obfuscatedPhoneNumber}.
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'Cabin',
                fontWeight: 400,
                fontSize: { mobile: 14, tablet: 16 },
                lineHeight: '24px',
                color: colors.csaaDark,
              }}
            >
              Didn&apos;t receive it?{' '}
              <Button
                disableRipple
                disableTouchRipple
                variant="text"
                sx={{
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  fontFamily: 'inherit',
                  px: 0,
                  py: 0,
                  width: 'fit-content',
                  alignSelf: 'flex-start',
                }}
                data-testid="resend-code-button"
                type="button"
                onClick={async () => {
                  track('Verification Code Resent', {
                    event_type: 'Link Accessed',
                    location: 'Feature Card',
                    registration_type: 'OTVC',
                    flow: 'SMS',
                    email: formValues.email,
                  });
                  await requestCode(getValues().phoneNumber);
                }}
              >
                Send a new code now
              </Button>
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'Cabin',
                fontWeight: 400,
                fontSize: { mobile: 14, tablet: 16 },
                lineHeight: '24px',
                color: colors.csaaDark,
              }}
            >
              Keep this page open to enter your code. If you close it,
              you&apos;ll need to start over with a new code.
            </Typography>
          </Stack>
        ) : (
          <Stack direction="column" spacing={2}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'Cabin',
                fontWeight: 400,
                fontSize: { mobile: 14, tablet: 16 },
                lineHeight: '24px',
                mt: 3,
                color: colors.csaaDark,
              }}
            >
              Enter the mobile phone number {obfuscatedPhoneNumber} associated
              with your policy. We&apos;ll send a one-time code to that number.
              If you don&apos;t have your phone, go back and select email.
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'Cabin',
                fontWeight: 400,
                fontSize: { mobile: 14, tablet: 16 },
                lineHeight: '24px',
                color: colors.csaaDark,
              }}
            >
              Keep this page open to enter your code. If you close this page,
              you&apos;ll need to start over with a new code.
            </Typography>
          </Stack>
        )}
        {submitted && (
          <ControlledTextField
            name="verificationCode"
            control={control}
            className="csaa-qm-block"
            label="Code"
            sx={{ mt: 3 }}
            preventWhitespace
            preventNonNumericValues
            handleBlur={(e, onBlur) => {
              inputBlurred('verificationCode');
              return onBlur(e);
            }}
            inputProps={{
              autoComplete: 'one-time-code',
              'data-testid': 'otvc-code-field',
              maxLength: 6,
            }}
            InputProps={{
              sx: { background: colors.csaaWhite },
              required: true,
            }}
          />
        )}
        {!submitted && (
          <ControlledTextField
            name="phoneNumber"
            control={control}
            className="csaa-qm-block"
            label="Mobile phone number"
            sx={{ mt: 3 }}
            preventWhitespace
            required
            handleBlur={(e, onBlur) => {
              inputBlurred('phoneNumber');
              return onBlur(e);
            }}
            inputProps={{
              autoComplete: 'tel',
              'data-testid': 'otvc-phone-number-field',
            }}
            InputProps={{
              inputComponent: PhoneNumberMask,
              sx: { background: colors.csaaWhite },
            }}
          />
        )}
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
            mt: { mobile: 3, tablet: 5 }
          }}>
          <Button
            type="submit"
            onClick={() => {
              window.scrollTo(0, 0);
            }}
            disabled={disableContinue}
            loading={
              sendCodeMutation.isPending || validateCodeMutation.isPending
            }
            data-testid="otvc-continue-button"
          >
            Continue
          </Button>
        </Stack>
      </Stack>
    </SplitView>
  );
};

export default SmsVerification;
